import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { injectScript } from '@atc/dom-fns';
import { useFeatures } from '@atc/features';
import { waitUntil } from '@atc/wait-until';

import { Alert } from 'reaxl';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import PsxOwnerDetailsSnapshot from '@/components/vehicledetails/PsxOwnerDetailsSnapshot';

import LoginListener from './LoginListener';
import PsxCtaContainer from './PsxCtaContainer';

/**
 * Skeleton of a container for the PSX leads web components (sidebar)
 */
function PsxLeadsContainer() {
    const dispatch = useDispatch();

    const [isError, setIsError] = useState(false);
    const isWebComponentReady = useSelector(vdpResultsDuck.selectors.getIsWebComponentReady);
    const {
        owner: {
            name,
            location: {
                address: {
                    city,
                    state,
                } = {},
            } = {},
        },
    } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};

    const {
        psx_cta_webcomponents: [, { version = '', branch = 'main' }],
        psx_config: [, { environment = 'prod' }],
    } = useFeatures([
        'psx_cta_webcomponents',
        'psx_config',
    ]);

    const handleScriptInjectionError = () => {
        setIsError(true);
    };
    const [isVerified, setIsVerified] = useState(false);

    useEffect(() => {
        (async () => {
            window.caiEventBus.on('psx.listingActions.sellerVerification', (e) => {
                if (e.isVerified) {
                    setIsVerified(true);
                }
            });

            await injectScript(`https://static.kbb.com/psx-listing-actions/${[branch, environment, version].filter(Boolean).join('/')}/psx-listing-actions.esm.js`, {
                elementAttributes: {
                    type: 'module',
                },
                rejectOnError: true,
            }).catch(handleScriptInjectionError);

            // Display psxWebComponent only when psxListingActions in caiEventBus are ready to listen
            const psxEventTypes = ['psx.listingActions.openTestDrive', 'psx.listingActions.openMakeOffer', 'psx.listingActions.openChat'];
            waitUntil(
                () => {
                    for (const eventType of psxEventTypes) {
                        if (!window.caiEventBus.all?.get(eventType)) {
                            return false;
                        }
                    }
                    return true;
                },
                () => dispatch(vdpResultsDuck.creators.setWebComponentAvailability(true)),
                200,
                3000,
                () => {
                    // eslint-disable-next-line no-console
                    console.error('Fail to initialize psxEventBus on the initial attempt');
                    dispatch(vdpResultsDuck.creators.setWebComponentAvailability(true));
                }
            );
        })();

        // Clean up eventBus and script tag when component unmount
        return () => {
            window.caiEventBus.off('psx.listingActions.openMakeOffer');
            document.querySelector('script[src*="static.kbb.com/psx-listing-actions"]')?.remove();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isError) {
        return (
            <Alert
                bsStyle="warning"
            >
                <strong>Our engines aren&rsquo;t firing on all cylinders.</strong>
                {' '}
                Try reloading this page to get chat, test drives and offers back on the road.
            </Alert>
        );
    }
    return typeof window !== 'undefined' && (
        <>
            <LoginListener />
            {isWebComponentReady && (
                <PsxCtaContainer
                    layout="uiLess"
                    isDirector
                />
            )}
            <PsxOwnerDetailsSnapshot
                name={name}
                city={city}
                showCTAs={isWebComponentReady}
                state={state}
                isSellerVerified={isVerified}
            />
        </>
    );

}

export default React.memo(PsxLeadsContainer);
